import { useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar, SegmentedFilter } from "@/components/ui/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { useAction, useList } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, relative, text } from "@/lib/format";
import { roleLabel } from "@/lib/labels";

interface Notification {
  id: string;
  rule_code: string;
  event_type: string;
  recipient_id: string;
  recipient_role: string;
  channel: string;
  language: string;
  subject?: string;
  body: string;
  priority: string;
  status: string;
  attempt_count: number;
  last_error?: string;
  sent_at?: string;
  read_at?: string;
  created_at: string;
}

interface Rule {
  code: string;
  role: string;
  priority: string;
  description: string;
  /** Shu rolda nechta faol xodim bor; 0 bo'lsa qoida ishlamaydi. */
  recipient_count: number;
}

type Tab = "all" | "failed" | "rules";

export function Notifications() {
  const session = useSession();
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [retrying, setRetrying] = useState<Notification | null>(null);

  const list = useList<Notification>(
    "notification",
    "/api/v1/notifications",
    { status: tab === "failed" ? "DEAD" : undefined, limit: 200 },
    { enabled: tab !== "rules" },
  );
  const rules = useList<Rule>(
    "notification",
    "/api/v1/notification-rules",
    {},
    { enabled: tab === "rules" },
  );

  const retry = useAction<Notification, Notification>("notification", (item) => ({
    path: `/api/v1/notifications/${item.id}/retry`,
    method: "POST",
  }));

  const canManage = session.can(PERM.notificationManage);
  const needle = search.trim().toLowerCase();
  const rows = (list.data?.items ?? []).filter((row) =>
    needle
      ? [row.rule_code, row.subject, row.body, row.recipient_role, row.channel]
          .some((value) => value?.toLowerCase().includes(needle))
      : true,
  );

  const columns: Array<Column<Notification>> = [
    { key: "rule", header: "Qoida", render: (row) => <StatusBadge value={row.rule_code} tone="tag" /> },
    { key: "role", header: "Rol", render: (row) => text(row.recipient_role) },
    { key: "channel", header: "Kanal", render: (row) => <StatusBadge value={row.channel} /> },
    {
      key: "priority",
      header: "Ustuvorlik",
      render: (row) => (
        <StatusBadge
          value={row.priority}
          tone={row.priority === "CRITICAL" ? "error" : row.priority === "HIGH" ? "warning" : "neutral"}
        />
      ),
    },
    { key: "subject", header: "Mavzu", render: (row) => text(row.subject) },
    { key: "status", header: "Holat", render: (row) => <StatusBadge value={row.status} /> },
    {
      key: "attempts",
      header: "Urinish",
      align: "right",
      render: (row) => <span className="num">{row.attempt_count}</span>,
    },
    {
      key: "error",
      header: "Sabab",
      // Yetkazilmagan xabarning SABABI ko'rsatiladi: administrator
      // nimani tuzatishi kerakligini bilishi shart.
      render: (row) =>
        row.last_error ? (
          <span style={{ color: "var(--error)" }} title={row.last_error}>
            {row.last_error.slice(0, 40)}
          </span>
        ) : (
          <span style={{ color: "var(--outline)" }}>—</span>
        ),
    },
    {
      key: "created",
      header: "Yaratildi",
      render: (row) => <span title={dateTime(row.created_at)}>{relative(row.created_at)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) =>
        (row.status === "DEAD" || row.status === "FAILED") && canManage ? (
          <button type="button" className="btn btn-outlined btn-sm" onClick={() => setRetrying(row)}>
            <Icon name="refresh" size={14} />
            Qayta
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Xabarnomalar"
        hint="NTF-001: LP nomuvofiqligi katta xodimga, to'liq bo'lmagan buyurtma menejerga, kechikkan sariq/qizil dispetcherga, integratsiya xatosi administratorga."
      />

      <Toolbar
        search={tab === "rules" ? undefined : search}
        onSearch={tab === "rules" ? undefined : setSearch}
        placeholder="Qoida, mavzu yoki matn bo'yicha…"
        onRefresh={() => void (tab === "rules" ? rules.refetch() : list.refetch())}
        refreshing={tab === "rules" ? rules.isFetching : list.isFetching}
      >
        <SegmentedFilter
          value={tab}
          onChange={setTab}
          options={[
            { value: "all", label: "Hammasi" },
            { value: "failed", label: "Yetkazilmagan" },
            { value: "rules", label: "NTF-001 marshrutlari" },
          ]}
        />
      </Toolbar>

      {tab === "rules" ? (
        <DataTable
          columns={[
            { key: "code", header: "Qoida", render: (row: Rule) => <StatusBadge value={row.code} tone="tag" /> },
            {
              key: "role",
              header: "Kimga",
              render: (row: Rule) => <b title={row.role}>{roleLabel(row.role)}</b>,
            },
            {
              key: "recipients",
              header: "Xodim",
              align: "right",
              // Rolda xodim bo'lmasa xabar UMUMAN yaratilmaydi va
              // hech qayerda ko'rinmaydi — buni oldindan ko'rsatamiz.
              render: (row: Rule) =>
                row.recipient_count > 0 ? (
                  <span className="num">{row.recipient_count}</span>
                ) : (
                  <StatusBadge value="xodim yo'q" tone="error" />
                ),
            },
            {
              key: "priority",
              header: "Ustuvorlik",
              render: (row: Rule) => (
                <StatusBadge
                  value={row.priority}
                  tone={row.priority === "CRITICAL" ? "error" : row.priority === "HIGH" ? "warning" : "neutral"}
                />
              ),
            },
            { key: "desc", header: "Tavsif", render: (row: Rule) => row.description },
          ]}
          rows={rules.data?.items}
          rowKey={(row) => row.code}
          isLoading={rules.isLoading}
          error={rules.error}
          onRetry={() => void rules.refetch()}
          emptyTitle="Qoidalar topilmadi"
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          emptyTitle={tab === "failed" ? "Yetkazilmagan xabar yo'q" : "Xabar yo'q"}
          emptyHint={
            tab === "failed"
              ? "Barcha xabarlar yetkazilgan yoki hali navbatda."
              : "Xabarlar hodisalardan avtomatik yaratiladi."
          }
          hasMore={list.data?.has_more}
          footer={<span>{rows.length} ta xabar</span>}
        />
      )}

      <ConfirmDialog
        open={retrying !== null}
        title="Xabarni qayta yuborish"
        description={
          retrying ? (
            <>
              <b>{retrying.rule_code}</b> xabari <b>{retrying.channel}</b> kanali
              orqali qayta yuboriladi. Sabab tuzatilmagan bo'lsa, u yana
              yetkazilmasligi mumkin.
            </>
          ) : null
        }
        confirmLabel="Qayta yuborish"
        busy={retry.isPending}
        error={retry.error}
        extra={
          retrying?.last_error ? (
            <div className="dialog-error">
              <Icon name="alert" size={16} />
              <span>{retrying.last_error}</span>
            </div>
          ) : null
        }
        onClose={() => {
          setRetrying(null);
          retry.reset();
        }}
        onConfirm={() => {
          if (!retrying) return;
          retry.mutate(retrying, {
            onSuccess: () => {
              setRetrying(null);
              retry.reset();
            },
          });
        }}
      />
    </>
  );
}
